import type { FormBlockMetadataById } from 'models/metadata/block.definition';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { FormIdProps } from 'shared/props';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import type { DataById } from 'state/formData/formData.model';
import {
  formInputDataChangedThunk,
  refreshScheduleThunk,
  resetDefaultDatesThunk,
} from 'state/formData/formData.thunks';
import { getBlocksMetadata, getFieldsMetadata } from 'state/forms/metadata/selectors';
import type { GimmicksByName, ProductGraph } from 'state/product-description/model';
import type { StaticProductView } from 'state/product-description/types';
import {
  getCombinedFormData,
  getGimmicksDescription,
  getIsUserInternal,
  getProductVariation,
  getStaticFormInfos,
  getStaticFormValidationErrors,
  getStaticPreviousQuoteIsLoading,
  getStaticWorkflowStatus,
  getUserDefaultProductView,
  getUserPreferencesBatchWorkspaceVersion,
  getUserPreferencesQuoteWorkspaceVersion,
  getWorkspaceStaticTabAiGeneratedFields,
  isPendingEnrichment,
  isStaticFormLoaded,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { staticFormTabRollbackedThunk, workspaceTabClonedThunk } from 'state/static/staticForm.thunks';
import type { BatchWorkspaceVersion, QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import { staticWorkspaceOpenInBatchWorkspaceThunk } from 'state/workspaces/static/static.thunks';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';
import { workspaceResetTabRequestedThunk } from 'state/workspaces/workspace.thunks';

interface StateProps {
  product?: string;
  productView: StaticProductView;
  productDescription?: ProductGraph;
  gimmicksDescription?: GimmicksByName;
  blocksMetadata: FormBlockMetadataById;
  fieldsMetadata: FieldMetadataById;
  combinedFormData: DataById;
  isLoading: boolean;
  isQuoted: boolean;
  validationErrors: readonly QuoteFormError[];
  isUserInternal: boolean;
  crossBrowserDirty: boolean;
  canReloadSession: boolean;
  canClone: boolean;
  canReset: boolean;
  canOpenInBatchWorkspace: boolean;

  isNotEditable: boolean;
  negotiationId?: string;
  isTabFrozen: boolean;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  mustForceAdvancedMode: boolean;
  aiGeneratedFields: string[];
}

interface DispatchProps {
  setFieldValue: (key: string, value: unknown) => void;
  onResetTabClicked: () => void;
  onRefreshDefaultDatesClicked: () => void;
  onRefreshScheduleClicked: () => void;
  onTabCloned: () => void;
  onTabOpenedInBatchWorkspace: (tabName: string) => void;
  onRollback: (negotiationId: string) => void;
}

export type StaticFormProps = FormIdProps & StateProps & DispatchProps;

const EMPTY_METADATA = {};
const mapStateToProps = (state: AppState, { formId }: FormIdProps): StateProps => {
  const quoteWorkspaceVersion = getUserPreferencesQuoteWorkspaceVersion(state);
  const batchWorkspaceVersion = getUserPreferencesBatchWorkspaceVersion(state);
  const productView = getUserDefaultProductView(state);
  const {
    productDescription: productSubType,
    crossBrowserDirty = false,
    mustForceAdvancedMode = false,
    negotiationId,
    instrumentName: productType,
  } = getStaticFormInfos(state, formId) ?? {};
  const productDescription =
    productSubType && productType
      ? getProductVariation(state, productView, { productSubType, productType })
      : undefined;
  const gimmicksDescription = getGimmicksDescription(state);
  const blocksMetadata = getBlocksMetadata(state, 'static', formId) ?? EMPTY_METADATA;
  const fieldsMetadata = getFieldsMetadata(state, 'static', formId) ?? EMPTY_METADATA;
  const combinedFormData = getCombinedFormData(state, formId);
  const isLoading = !isStaticFormLoaded(state, formId);
  const quoteWorkflowStatus = getStaticWorkflowStatus(state, formId);
  const isQuoting = quoteWorkflowStatus === 'QUOTING';
  const isQuoted =
    quoteWorkflowStatus === 'QUOTED' ||
    quoteWorkflowStatus === 'QUOTED_TRADABLE' ||
    quoteWorkflowStatus === 'TRADE_AUTH_REJECTED' ||
    quoteWorkflowStatus === 'TRADING' ||
    quoteWorkflowStatus === 'TRADED';
  const validationErrors = getStaticFormValidationErrors(state, formId);
  const isUserInternal = getIsUserInternal(state);
  const isLoadingPreviousQuote = getStaticPreviousQuoteIsLoading(state, formId);
  const isTabFrozen = getTabIsFrozen(state, formId) ?? false;
  const isPerformingEnrichment = isPendingEnrichment(state, formId);
  const canReset =
    !isLoading &&
    !isPerformingEnrichment &&
    !isQuoting &&
    !isTabFrozen &&
    !isLoadingPreviousQuote &&
    !crossBrowserDirty;
  const canClone = !isLoading && !isPerformingEnrichment && !crossBrowserDirty;
  const canOpenInBatchWorkspace = isQuoted;
  const isNotEditable = isTabFrozen || crossBrowserDirty || isLoadingPreviousQuote;
  const canReloadSession = !isTabFrozen;
  const aiGeneratedFields = getWorkspaceStaticTabAiGeneratedFields(state, formId);

  return {
    product: productSubType,
    productView,
    productDescription,
    gimmicksDescription,
    blocksMetadata,
    fieldsMetadata,
    combinedFormData,
    isLoading,
    canReset,
    canClone,
    canOpenInBatchWorkspace,
    isQuoted,
    validationErrors,
    isUserInternal,
    crossBrowserDirty,
    negotiationId,
    isNotEditable,
    canReloadSession,
    isTabFrozen,
    quoteWorkspaceVersion,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
    aiGeneratedFields,
  };
};

function mapDispatchToProps(dispatch: ThunkDispatch, { formId }: FormIdProps): DispatchProps {
  return {
    setFieldValue: (key: string, value: unknown) => {
      dispatch(formInputDataChangedThunk(formId, formId, key, value as Readonly<unknown>));
    },
    onResetTabClicked: () => {
      dispatch(workspaceResetTabRequestedThunk(formId, 'static'));
    },
    onRefreshScheduleClicked: () => {
      dispatch(refreshScheduleThunk(formId, formId));
    },
    onRefreshDefaultDatesClicked: () => {
      dispatch(resetDefaultDatesThunk(formId, formId));
    },
    onTabCloned() {
      dispatch(workspaceTabClonedThunk(formId, 'static', 'quote'));
    },
    onTabOpenedInBatchWorkspace(tabName: string) {
      dispatch(staticWorkspaceOpenInBatchWorkspaceThunk(formId, tabName));
      logAnalytics.trackEvent('Primary market static', 'Open in batch workspace');
    },
    onRollback(negotiationId: string) {
      dispatch(staticFormTabRollbackedThunk(negotiationId, formId));
    },
  };
}

export const connectStaticForm = connect(mapStateToProps, mapDispatchToProps);
